import React, { useState, useEffect } from 'react'
import classnames from 'classnames'
import { useSelector, useDispatch } from 'react-redux'

import { googlePayInitReadyToPay } from 'credit-cards/google-pay/actions'
import { getCreditCardShortCode } from 'shared/utils'
import { CardSection } from 'shared/components/CardSection'
import { CardSectionButton } from 'shared/components/CardSectionButton'
import Select from 'shared/components/Select'
import Button from 'shared/components/Button'
import AddCreditCardModal from 'shared/components/AddCreditCardModal'
import featureToggles from 'shared/featureToggles'
import { PIX_PAYMENT_METHOD, PSE_PAYMENT_METHOD, GOOGLE_PAY_METHOD, getPaymentMethod } from 'shared/transactions'
import { googlePayDisabledSelector } from 'credit-cards/google-pay/selectors'
import { creditCardSuccessfullyAddedSelector } from 'credit-cards/selectors'
import { sendAmplitudeEvent, useSendGTAGEvent } from 'analytics/utils'
import { CREDIT_CARD_PAYMENT_METHOD } from 'booking/constants'

import PixLogo from 'shared/assets/PixPayment.png'
import PseLogo from 'shared/assets/PsePayment.png'
import GPayLogo from 'shared/assets/GPay.png'

import styles from './styles.module.scss'

const getCardDisplayName = card =>
    card && !card?.lastFour ? (
        <T>guest.booking.review.pay.card.added</T>
    ) : (
        `${getCreditCardShortCode(card?.cardType)} **** ${card?.lastFour || card?.maskedPan}`
    )

const getPaymentSelectionState = (paymentMethods, selectedPaymentMethod) => {
    const selectedId = selectedPaymentMethod?.id
    const hasPaymentMethods = Array.isArray(paymentMethods) && paymentMethods.length > 0

    const pixMethodSelected = selectedId === PIX_PAYMENT_METHOD
    const pseMethodSelected = selectedId === PSE_PAYMENT_METHOD
    const googlePaySelected = selectedId === GOOGLE_PAY_METHOD
    const creditCardSelected =
        hasPaymentMethods && !!selectedId && !pixMethodSelected && !pseMethodSelected && !googlePaySelected

    return {
        pixMethodSelected,
        pseMethodSelected,
        googlePaySelected,
        creditCardSelected,
    }
}

const PaymentMethodSection = ({
    paymentMethods,
    setPaymentMethod,
    selectedPaymentMethod,
    showPixPaymentMethod,
    guestUserFlow,
    removeGuestUserCard,
    pseBanks,
    hasGooglePay,
    hideCreditCardPayment = false,
    analyticsFlowType,
    translate: t,
}) => {
    const sendGTAGEvent = useSendGTAGEvent()
    const [showAddCardModal, setShowAddCardModal] = useState(false)
    const dispatch = useDispatch()
    const googlePayDisabled = useSelector(googlePayDisabledSelector) && !hasGooglePay
    const cardIsSuccessfullyAdded = useSelector(creditCardSuccessfullyAddedSelector)
    const { pixMethodSelected, pseMethodSelected, googlePaySelected, creditCardSelected } = getPaymentSelectionState(
        paymentMethods,
        selectedPaymentMethod
    )

    const title = featureToggles.supportsDebitCard
        ? 'subscription.purchase.label.title.withdebit'
        : 'subscription.purchase.label.title'

    const showRemoveCardButton = guestUserFlow && paymentMethods?.length > 0

    useEffect(() => {
        if (guestUserFlow) {
            if (paymentMethods) setPaymentMethod(paymentMethods[0])
            else if (showPixPaymentMethod) setPaymentMethod({ id: PIX_PAYMENT_METHOD })
        }
    }, [paymentMethods])

    useEffect(() => {
        if (cardIsSuccessfullyAdded && analyticsFlowType) {
            sendGTAGEvent('add_payment_method', {
                payment_type: CREDIT_CARD_PAYMENT_METHOD,
                flow_type: analyticsFlowType,
            })
            sendAmplitudeEvent('Payment Method Added', {
                'Payment Method': CREDIT_CARD_PAYMENT_METHOD,
                'Flow Type': analyticsFlowType,
            })
        }
    }, [cardIsSuccessfullyAdded])

    useEffect(() => {
        if (hasGooglePay) {
            dispatch(googlePayInitReadyToPay())
        }
    }, [])

    const onPaymentMethodClick = paymentMethod => {
        setPaymentMethod(paymentMethod)
        sendGTAGEvent('payment_method_selected', {
            payment_type: getPaymentMethod(paymentMethod?.id),
            flow_type: analyticsFlowType,
        })
        sendAmplitudeEvent('Payment Method Selected', {
            'Payment Type': getPaymentMethod(paymentMethod?.id),
            'Flow Type': analyticsFlowType,
        })
    }

    const RemoveCardButton = () => (
        <Button
            size="small"
            type="button"
            btnStyle="link"
            iconLeft="minus-round"
            onClick={removeGuestUserCard}
            className="mb-3">
            <T>guest.booking.review.pay.remove.credit.card.label</T>
        </Button>
    )
    const AddCardButton = () => (
        <Button
            size="small"
            type="button"
            btnStyle="link"
            iconLeft="plus-round"
            onClick={() => setShowAddCardModal(true)}
            className="mb-3">
            <T>dashboard.label.add.credit.card</T>
        </Button>
    )
    return (
        <>
            {showAddCardModal && (
                <AddCreditCardModal onClose={() => setShowAddCardModal(false)} isGuestFlow={guestUserFlow} />
            )}
            <div className="mt-5">
                <h3 className="font-weight-bold text-uppercase mb-4">
                    <T>subscription.purchase.label.payment.method</T>
                </h3>
                {!hideCreditCardPayment && (
                    <CardSection activeSelected={creditCardSelected} notActive className="px-3 py-3 mt-6 mb-6">
                        <div className="d-sm-flex justify-content-between">
                            <div className={classnames('d-flex', 'flex-column', styles.paymentSelectionTitle)}>
                                <span className="font-weight-bold">
                                    <T>{title}</T>
                                </span>
                            </div>
                            <div className="d-flex flex-wrap flex-row">
                                {paymentMethods &&
                                    paymentMethods.map((p, i) => (
                                        <CardSectionButton
                                            key={i}
                                            onClick={() => onPaymentMethodClick(p)}
                                            value={getCardDisplayName(p)}
                                            description={p?.nickName}
                                            isSelected={p?.id === selectedPaymentMethod?.id}
                                            className={styles.paymentMethodBtn}
                                        />
                                    ))}
                            </div>
                        </div>
                    </CardSection>
                )}
                {showPixPaymentMethod && (
                    <CardSection activeSelected={pixMethodSelected} notActive className="px-3 py-3 mt-6 mb-6">
                        <div
                            onClick={() => onPaymentMethodClick({ id: PIX_PAYMENT_METHOD })}
                            className="d-flex justify-content-between">
                            <span className="font-weight-bold">
                                <T>subscription.purchase.pix.label</T>
                            </span>
                            <CardSectionButton
                                isSelected={pixMethodSelected}
                                className={classnames(
                                    styles.pixPaymentMethodBtn,
                                    pixMethodSelected && styles.pixPaymentMethodBtnActive
                                )}>
                                <img src={PixLogo} alt={t('subscription.purchase.pix.label')} />
                            </CardSectionButton>
                        </div>
                    </CardSection>
                )}
                {featureToggles.hasPsePurchase && pseBanks && (
                    <CardSection activeSelected={pseMethodSelected} notActive className="px-3 py-3 mt-6 mb-6">
                        <div className="d-sm-flex justify-content-between">
                            <div className="d-flex align-items-center">
                                <img src={PseLogo} alt={t('payment.method.pse.label')} />
                                <span className="font-weight-bold ml-1">
                                    <T>payment.method.pse.label</T>
                                </span>
                            </div>
                            <div className={styles.psePaymentMethodContainer}>
                                <Select
                                    options={pseBanks}
                                    onChange={option =>
                                        onPaymentMethodClick({ id: PSE_PAYMENT_METHOD, bankCode: option.value })
                                    }
                                />
                            </div>
                        </div>
                    </CardSection>
                )}
                {!googlePayDisabled && (
                    <CardSection notActive activeSelected={googlePaySelected} className="px-3 py-3 mt-6 mb-6">
                        <div
                            onClick={() => onPaymentMethodClick({ id: GOOGLE_PAY_METHOD })}
                            className="d-flex justify-content-between">
                            <span className="font-weight-bold">
                                <T>payment.method.google.pay.label</T>
                            </span>
                            <div
                                onClick={() => onPaymentMethodClick({ id: GOOGLE_PAY_METHOD })}
                                className={styles.gPayPaymentMethodBtn}>
                                <img src={GPayLogo} alt={t('payment.method.google.pay.label')} />
                            </div>
                        </div>
                    </CardSection>
                )}
                {!hideCreditCardPayment && (showRemoveCardButton ? <RemoveCardButton /> : <AddCardButton />)}
            </div>
        </>
    )
}

export default withTranslate(PaymentMethodSection)
